import React, { useEffect, useState } from 'react'
import { ModernLayout } from './components/layout/ModernLayout'
import { SimpleOverview } from './components/pages/SimpleOverview'
import { ResearchHub } from './components/pages/ResearchHub'
import { ValidationHub } from './components/pages/ValidationHub'
import { ModernTradingHub } from './components/pages/ModernTradingHub'
import { useDashboardStore } from './store/dashboardStore'
import { useDataLoader } from './hooks/useDataLoader'
import './styles/index.css'

type PageType = 'overview' | 'research' | 'validation' | 'trading'

function App() {
  const [activePage, setActivePage] = useState<PageType>('overview')
  const { connectionStatus, lastUpdate, isLoading, error } = useDashboardStore()
  
  // Initialize data loading with automatic refresh
  const { 
    refresh, 
    forceReload, 
    getLoadingState, 
    clearError 
  } = useDataLoader({
    autoLoad: true,
    enableRefresh: true,
    refreshInterval: 60000, // Refresh every minute
    maxRetries: 3
  })
  
  // Removed WebSocket connection to prevent connection errors
  // Real-time updates are handled through periodic refresh instead

  // Handle visibility change to refresh data when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && connectionStatus === 'connected') {
        const loadingState = getLoadingState()
        const timeSinceLastLoad = loadingState.lastLoadTime 
          ? Date.now() - loadingState.lastLoadTime.getTime()
          : Infinity
        
        // Refresh if it's been more than 5 minutes since last load
        if (timeSinceLastLoad > 5 * 60 * 1000) {
          refresh()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [connectionStatus, getLoadingState, refresh])

  // Error recovery handler
  const handleErrorRetry = () => {
    clearError()
    forceReload()
  }

  const renderActivePage = () => {
    switch (activePage) {
      case 'overview':
        return <SimpleOverview />
      case 'research':
        return <ResearchHub />
      case 'validation':
        return <ValidationHub />
      case 'trading':
        return <ModernTradingHub />
      default:
        return <SimpleOverview />
    }
  }

  return (
    <ModernLayout 
      activePage={activePage} 
      onPageChange={setActivePage}
      connectionStatus={connectionStatus}
      lastUpdate={lastUpdate}
      isLoading={isLoading}
      error={error}
      onRefresh={refresh}
      onRetry={handleErrorRetry}
    >
      {renderActivePage()}
    </ModernLayout>
  )
}

export default App